if (!customElements.get("countdown-timer")) {
  class CountdownTimer extends HTMLElement {
    constructor() {
      super();
      // A timeout, not an animation frame. The display only changes once a second, and a
      // continuous rAF loop keeps the main thread busy enough that requestIdleCallback never
      // gets a turn — which silently broke collection filtering, since the theme's view
      // transition waits on an idle callback before re-rendering the section.
      this.timerId = null;
      this.countDownDate = this.#parseDate();
    }

    #parseDate() {
      const { date, time = "0:0", timezone, timeZone } = this.dataset;
      const [day, month, year] = date.split("-").map(Number);
      const [hour, min] = time.split(":").map(Number);

      if (timeZone) {
        return CountdownTimer.#dateInTimeZone(year, month, day, hour, min, timeZone);
      }

      return timezone
        ? new Date(`${month}/${day}/${year} ${hour}:${min} GMT${timezone}`).getTime()
        : new Date(year, month - 1, day, hour, min).getTime();
    }

    static #dateInTimeZone(year, month, day, hour, minute, timeZone) {
      const wallTime = Date.UTC(year, month - 1, day, hour, minute);
      let deadline = wallTime;

      try {
        const formatter = new Intl.DateTimeFormat("en-US", {
          timeZone,
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
          hourCycle: "h23",
        });

        // Match the requested wall-clock time using the offset on that date, including DST.
        for (let attempt = 0; attempt < 3; attempt++) {
          const parts = {};
          for (const { type, value } of formatter.formatToParts(deadline)) {
            if (type !== "literal") parts[type] = Number(value);
          }
          const localTime = Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute);
          const adjustment = wallTime - localTime;
          if (adjustment === 0) return deadline;
          deadline += adjustment;
        }
      } catch {
        // Unsupported zones and nonexistent local times leave the opted-in bar hidden.
      }

      return NaN;
    }

    connectedCallback() {
      this.els = {
        days: this.querySelector(".days .countdown-timer--column--number"),
        hours: this.querySelector(".hours .countdown-timer--column--number"),
        minutes: this.querySelector(".minutes .countdown-timer--column--number"),
        seconds: this.querySelector(".seconds .countdown-timer--column--number"),
      };

      if (this.dataset.timeZone && !Number.isFinite(this.countDownDate)) return;
      this.#tick();
      if (this.dataset.timeZone) this.closest(".vp-timer-bar")?.removeAttribute("hidden");
    }

    disconnectedCallback() {
      clearTimeout(this.timerId);
    }

    #tick() {
      const distance = this.countDownDate - Date.now();

      if (distance < 0) {
        this.#expire();
        return;
      }

      this.#render(distance);
      // Land on the next whole second so the digits do not appear to skip or stall.
      this.timerId = setTimeout(() => this.#tick(), 1000 - (Date.now() % 1000));
    }

    #render(distance) {
      const { days, hours, minutes, seconds } = this.els;

      days.textContent = CountdownTimer.#pad(Math.floor(distance / 86_400_000));
      hours.textContent = CountdownTimer.#pad(Math.floor((distance % 86_400_000) / 3_600_000));
      minutes.textContent = CountdownTimer.#pad(Math.floor((distance % 3_600_000) / 60_000));
      seconds.textContent = CountdownTimer.#pad(Math.floor((distance % 60_000) / 1000));
    }

    #expire() {
      clearTimeout(this.timerId);

      const { days, hours, minutes, seconds } = this.els;
      days.textContent = hours.textContent = minutes.textContent = seconds.textContent = "00";

      this.classList.add("is-expired");
      this.closest(".vp-count-down-hero-banner")?.classList.add("expired");
    }

    static #pad(n) {
      return n < 10 ? `0${n}` : `${n}`;
    }
  }

  customElements.define("countdown-timer", CountdownTimer);
}
